/**
 * PNG export functionality for stage layouts
 * Renders stage to high-resolution PNG for export and thumbnails
 */

import {
  renderGrid,
  renderWall,
  renderPosition,
  renderObject,
  renderAnnotation,
  clearCanvas,
} from '@/canvas/renderer';
import { Vec2 } from '@/canvas/coordinates';
import type { StageLayout } from '@/stage/types';

interface ExportOptions {
  width: number;
  height: number;
  scale: number;
  includeGrid?: boolean;
}

/**
 * Export the current stage layout to a high-resolution PNG
 */
export async function exportCanvasPng(
  layout: StageLayout,
  options: ExportOptions
): Promise<Blob> {
  const { width, height, scale, includeGrid = true } = options;

  // Create offscreen canvas
  const offscreenCanvas = new OffscreenCanvas(
    width * scale,
    height * scale
  );
  const ctx = offscreenCanvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Set up rendering context
  ctx.scale(scale, scale);

  // Clear with white background
  clearCanvas(ctx as unknown as CanvasRenderingContext2D, width, height, '#ffffff');

  // Calculate appropriate zoom level to fit stage
  const canvasAspect = width / height;
  const stageAspect =
    layout.canvas.width_m / layout.canvas.height_m;

  let zoom: number;
  if (canvasAspect > stageAspect) {
    zoom = (height * 0.9) / layout.canvas.height_m;
  } else {
    zoom = (width * 0.9) / layout.canvas.width_m;
  }

  // Center the stage in the canvas
  const panOffset: Vec2 = {
    x: (width - layout.canvas.width_m * zoom) / 2,
    y: (height - layout.canvas.height_m * zoom) / 2,
  };

  const renderCtx = ctx as unknown as CanvasRenderingContext2D;

  // Render grid if requested
  if (includeGrid) {
    renderGrid(
      renderCtx,
      layout.canvas,
      zoom,
      panOffset,
      width,
      height,
      layout.canvas.grid_size_m
    );
  }

  // Render walls
  for (const wall of layout.walls) {
    renderWall(renderCtx, wall, zoom, panOffset, false);
  }

  // Render shooting positions
  for (const pos of layout.shooting_positions) {
    renderPosition(renderCtx, pos, zoom, panOffset, false);
  }

  // Render stage objects
  for (const obj of layout.objects) {
    renderObject(renderCtx, obj, zoom, panOffset, false, false);
  }

  // Render annotations
  for (const ann of layout.annotations) {
    renderAnnotation(renderCtx, ann, zoom, panOffset);
  }

  // Convert to blob
  const blob = await offscreenCanvas.convertToBlob({
    type: 'image/png',
    quality: 1,
  });

  return blob;
}

/**
 * Generate a thumbnail image (400x300) for the stage
 */
export async function generateThumbnail(
  layout: StageLayout
): Promise<Blob> {
  return exportCanvasPng(layout, {
    width: 400,
    height: 300,
    scale: 1,
    includeGrid: false,
  });
}

/**
 * Convert a blob to a data URL for embedding in PDF
 */
export async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Trigger download of a blob
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
